/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Reducer for Settings Profile component
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.3.0
 */

import produce from 'immer';
import omit from 'lodash/omit';
import { Reducer } from 'redux';

import {
    ESettingsProfileComponents,
    ESettingsProfilePasswordError,
    ESettingsProfilePasswordFields,
    SETTINGS_PROFILE_LOAD_META_FAILURE,
    SETTINGS_PROFILE_LOAD_META_SUCCESS,
    SETTINGS_PROFILE_RESET_PARAMS_ACTION,
    SETTINGS_PROFILE_SAVE_COMMON_FAILURE,
    SETTINGS_PROFILE_SAVE_COMMON_SUCCESS,
    SETTINGS_PROFILE_SAVE_PASSWORD_ACTION,
    SETTINGS_PROFILE_SAVE_PASSWORD_FAILURE,
    SETTINGS_PROFILE_SAVE_PASSWORD_SUCCESS,
    SETTINGS_PROFILE_SAVE_QUESTION_ACTION,
    SETTINGS_PROFILE_SAVE_QUESTION_FAILURE,
    SETTINGS_PROFILE_SAVE_QUESTION_SUCCESS,
    SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_UPDATED_ACTION,
    SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_VALID_ACTION,
    SETTINGS_PROFILE_SET_IS_PREFERRED_LK_PARAMS_UPDATED_ACTION,
    SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION,
    SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION,
    SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION,
    SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION,
    SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION,
} from './settingsProfileConstants';
import {
    ISettingsProfileMeta,
    ISettingsProfileMetaContacts,
    ISettingsProfileMetaHints,
    ISettingsProfileMetaPassword,
    ISettingsProfileMetaPreferredLK,
    ISettingsProfileMetaQuestion,
    ISettingsProfileParams,
    ISettingsProfileParamsContacts,
    ISettingsProfileParamsHints,
    ISettingsProfileParamsOnboarding,
    ISettingsProfileParamsPassword,
    ISettingsProfileParamsQuestion,
    ISettingsProfilePreferredLK,
    ISettingsProfileQuestionItem,
    TSettingsProfileActionTypes,
} from './settingsProfileTypes';

export interface ISettingsProfileReducer {
    errorMessage: string;
    isLoadingModal: boolean;
    isCommonParamsUpdated: boolean;
    isPreferredLKParamsUpdated: boolean;
    isCommonParamsValid: boolean;
    isRecoveryAvailable: boolean;
    meta: ISettingsProfileMeta;
    params: ISettingsProfileParams;
}

export const initialSelectedQuestion: ISettingsProfileQuestionItem = {
    answerMinLength: 0,
    questionId: 0,
    questionText: '',
};

export const initialSettingsProfileMetaContacts: ISettingsProfileMetaContacts = {
    emailIsEnabled: false,
    email: '',
    phoneIsEnabled: false,
    phone: '',
};

export const initialSettingsProfileParamsContacts: ISettingsProfileParamsContacts = {
    ...initialSettingsProfileMetaContacts,
    emailErrorCode: '',
    phoneErrorCode: '',
};

export const initialSettingsProfileMetaPreferredLK: ISettingsProfilePreferredLK = {
    preferred: null,
};

export const initialSettingsProfilePreferredLK: ISettingsProfileMetaPreferredLK = {
    ...initialSettingsProfileMetaPreferredLK,
};

export const initialSettingsProfileMetaHints: ISettingsProfileMetaHints = {
    hintsIsEnabled: false,
};

export const initialSettingsProfileParamsHints: ISettingsProfileParamsHints = {
    ...initialSettingsProfileMetaHints,
};

export const initialSettingsProfileMetaOnboarding: ISettingsProfileParamsOnboarding = {
    onboardingIsDisabled: false,
};

export const initialSettingsProfileParamsOnboarding: ISettingsProfileParamsOnboarding = {
    ...initialSettingsProfileMetaOnboarding,
};

export const initialSettingsProfileMetaPassword: ISettingsProfileMetaPassword = {
    alphabet: '',
    maxLength: 0,
    minLength: 0,
    requiredDigit: false,
    requiredLetter: false,
};

export const initialSettingsProfileParamsPassword: ISettingsProfileParamsPassword = {
    [ESettingsProfilePasswordFields.CURRENT]: '',
    [ESettingsProfilePasswordError.CURRENT]: '',
    [ESettingsProfilePasswordFields.NEW]: '',
    [ESettingsProfilePasswordError.NEW]: '',
    [ESettingsProfilePasswordFields.REPEAT]: '',
    [ESettingsProfilePasswordError.REPEAT]: '',
};

export const initialSettingsProfileMetaQuestion: ISettingsProfileMetaQuestion = {
    currentQuestion: '',
    questionList: [],
};

export const initialSettingsProfileParamsQuestion: ISettingsProfileParamsQuestion = {
    answer: '',
    answerErrorCode: '',
    isLoading: false,
    selectedQuestion: initialSelectedQuestion,
};

export const initialSettingsProfileMeta: ISettingsProfileMeta = {
    [ESettingsProfileComponents.CONTACTS]: initialSettingsProfileMetaContacts,
    [ESettingsProfileComponents.PREFERRED_LK]: initialSettingsProfileMetaPreferredLK,
    [ESettingsProfileComponents.HINTS]: initialSettingsProfileMetaHints,
    [ESettingsProfileComponents.ONBOARDING]: initialSettingsProfileMetaOnboarding,
    [ESettingsProfileComponents.PASSWORD]: initialSettingsProfileMetaPassword,
    [ESettingsProfileComponents.QUESTION]: initialSettingsProfileMetaQuestion,
};

export const initialSettingsProfileParams: ISettingsProfileParams = {
    [ESettingsProfileComponents.CONTACTS]: initialSettingsProfileParamsContacts,
    [ESettingsProfileComponents.PREFERRED_LK]: initialSettingsProfilePreferredLK,
    [ESettingsProfileComponents.HINTS]: initialSettingsProfileParamsHints,
    [ESettingsProfileComponents.ONBOARDING]: initialSettingsProfileParamsOnboarding,
    [ESettingsProfileComponents.PASSWORD]: initialSettingsProfileParamsPassword,
    [ESettingsProfileComponents.QUESTION]: initialSettingsProfileParamsQuestion,
};

export const initialSettingsProfileState: ISettingsProfileReducer = {
    isLoadingModal: false,
    isCommonParamsUpdated: false,
    isPreferredLKParamsUpdated: false,
    isCommonParamsValid: true,
    isRecoveryAvailable: false,
    errorMessage: '',
    meta: initialSettingsProfileMeta,
    params: initialSettingsProfileParams,
};

export const settingsProfileReducer: Reducer<ISettingsProfileReducer, TSettingsProfileActionTypes> = (
    state: ISettingsProfileReducer = initialSettingsProfileState,
    action: TSettingsProfileActionTypes,
) =>
    produce(state, (draft) => {
        switch (action.type) {
            case SETTINGS_PROFILE_LOAD_META_SUCCESS:
                draft.meta = { ...draft.meta, ...action.payload.meta };
                draft.isRecoveryAvailable = action.payload.isRecoveryAvailable;
                break;
            case SETTINGS_PROFILE_RESET_PARAMS_ACTION:
                if (action.payload.params.includes(ESettingsProfileComponents.CONTACTS)) {
                    draft.params[ESettingsProfileComponents.CONTACTS] = {
                        ...initialSettingsProfileParamsContacts,
                        ...draft.meta[ESettingsProfileComponents.CONTACTS],
                    };
                }
                if (action.payload.params.includes(ESettingsProfileComponents.PREFERRED_LK)) {
                    draft.params[ESettingsProfileComponents.PREFERRED_LK] = {
                        ...initialSettingsProfilePreferredLK,
                        ...draft.meta[ESettingsProfileComponents.PREFERRED_LK],
                    };
                }
                if (action.payload.params.includes(ESettingsProfileComponents.HINTS)) {
                    draft.params[ESettingsProfileComponents.HINTS] = {
                        ...initialSettingsProfileParamsHints,
                        ...draft.meta[ESettingsProfileComponents.HINTS],
                    };
                }
                if (action.payload.params.includes(ESettingsProfileComponents.ONBOARDING)) {
                    draft.params[ESettingsProfileComponents.ONBOARDING] = {
                        ...initialSettingsProfileParamsOnboarding,
                        ...draft.meta[ESettingsProfileComponents.ONBOARDING],
                    };
                }
                if (action.payload.params.includes(ESettingsProfileComponents.PASSWORD)) {
                    draft.params[ESettingsProfileComponents.PASSWORD] = { ...initialSettingsProfileParamsPassword };
                }
                if (action.payload.params.includes(ESettingsProfileComponents.QUESTION)) {
                    draft.params[ESettingsProfileComponents.QUESTION] = { ...initialSettingsProfileParamsQuestion };
                }
                break;
            case SETTINGS_PROFILE_LOAD_META_FAILURE:
            case SETTINGS_PROFILE_SAVE_COMMON_FAILURE:
                draft.errorMessage = action.payload.message;
                break;
            case SETTINGS_PROFILE_SET_IS_PREFERRED_LK_PARAMS_UPDATED_ACTION:
                draft.isPreferredLKParamsUpdated = action.payload.isPreferredLKParamsUpdated;
                break;
            case SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_UPDATED_ACTION:
                draft.isCommonParamsUpdated = action.payload.isCommonParamsUpdated;
                break;
            case SETTINGS_PROFILE_SET_IS_COMMON_PARAMS_VALID_ACTION:
                draft.isCommonParamsValid = action.payload.isCommonParamsValid;
                break;
            case SETTINGS_PROFILE_SET_PARAM_CONTACTS_ACTION:
                draft.params[ESettingsProfileComponents.CONTACTS] = {
                    ...draft.params[ESettingsProfileComponents.CONTACTS],
                    ...action.payload,
                };
                break;
            case SETTINGS_PROFILE_SET_PARAM_PREFERRED_LK_ACTION:
                draft.params[ESettingsProfileComponents.PREFERRED_LK] = action.payload;
                break;
            case SETTINGS_PROFILE_SET_PARAM_COMPONENTS_ACTION:
                const { component, ...params } = action.payload;
                if (component === ESettingsProfileComponents.HINTS) {
                    draft.params[ESettingsProfileComponents.HINTS].hintsIsEnabled = params.hintsIsEnabled || false;
                }
                if (component === ESettingsProfileComponents.ONBOARDING) {
                    draft.params[ESettingsProfileComponents.ONBOARDING].onboardingIsDisabled =
                        params.onboardingIsDisabled || false;
                }
                break;
            case SETTINGS_PROFILE_SET_PARAM_PASSWORD_ACTION:
                draft.params[ESettingsProfileComponents.PASSWORD] = {
                    ...draft.params[ESettingsProfileComponents.PASSWORD],
                    ...action.payload,
                };
                break;
            case SETTINGS_PROFILE_SET_PARAM_QUESTION_ACTION:
                draft.params[ESettingsProfileComponents.QUESTION] = {
                    ...draft.params[ESettingsProfileComponents.QUESTION],
                    ...action.payload,
                };
                break;
            case SETTINGS_PROFILE_SAVE_COMMON_SUCCESS:
                draft.meta[ESettingsProfileComponents.CONTACTS] = {
                    ...omit(draft.params[ESettingsProfileComponents.CONTACTS], ['emailErrorCode', 'phoneErrorCode']),
                };
                draft.meta[ESettingsProfileComponents.PREFERRED_LK] = {
                    ...draft.params[ESettingsProfileComponents.PREFERRED_LK],
                };
                draft.meta[ESettingsProfileComponents.HINTS] = { ...draft.params[ESettingsProfileComponents.HINTS] };
                draft.meta[ESettingsProfileComponents.ONBOARDING] = {
                    ...draft.params[ESettingsProfileComponents.ONBOARDING],
                };
                draft.isCommonParamsUpdated = false;
                draft.isPreferredLKParamsUpdated = false;
                break;
            case SETTINGS_PROFILE_SAVE_PASSWORD_ACTION:
            case SETTINGS_PROFILE_SAVE_QUESTION_ACTION:
                draft.isLoadingModal = true;
                break;
            case SETTINGS_PROFILE_SAVE_QUESTION_FAILURE:
            case SETTINGS_PROFILE_SAVE_PASSWORD_FAILURE:
                draft.errorMessage = action.payload.message;
                draft.isLoadingModal = false;
                break;
            case SETTINGS_PROFILE_SAVE_QUESTION_SUCCESS:
                draft.meta[ESettingsProfileComponents.QUESTION].currentQuestion = action.payload.selectedQuestion;
                draft.params[ESettingsProfileComponents.QUESTION] = { ...initialSettingsProfileParamsQuestion };
                draft.isLoadingModal = false;
                break;
            case SETTINGS_PROFILE_SAVE_PASSWORD_SUCCESS:
                draft.params[ESettingsProfileComponents.PASSWORD] = { ...initialSettingsProfileParamsPassword };
                draft.isLoadingModal = false;
                break;
            default:
                break;
        }
    });
